import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { OnDestroyBase } from '@os-components';
import { SewWsPageContentTeaserSimple, SewWsPodcastHumian } from '@sew/ui-components-angular/standalone';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ContentType } from '../../enums/content-types.enum';
import { CustomBreakpoint } from '../../helpers/breakpoint.helper';
import { Content, Margin } from '../../models/blog-posts.model';
import { BlogLabels } from '../../models/label.model';
import { BlogService } from '../../services/blog/blog.service';
import { FormTabletComponent } from '../form/form-tablet/form-tablet.component';
import { FormComponent } from '../form/form.component';
import { FsAddSectionComponent } from '../fs/fs-add-section/fs-add-section.component';
import { ModalComponent } from '../modal/modal.component';
import { ModalService } from '../modal/modal.service';
import { AudioComponent } from './components/audio/audio.component';
import { CiteComponent } from './components/cite/cite.component';
import { CtaComponent } from './components/cta/cta.component';
import { HighlightComponent } from './components/highlight/highlight.component';
import { PictureComponent } from './components/picture/picture.component';
import { SocialMediaComponent } from './components/social-media/social-media.component';
import { TextComponent } from './components/text/text.component';
import { YoutubePlayerComponent } from './components/youtube-player/youtube-player.component';

@Component({
    selector: 'blog-content',
    templateUrl: './content.component.html',
    styleUrls: ['./content.component.scss'],
    imports: [
        CommonModule,
        FormComponent,
        SocialMediaComponent,
        TextComponent,
        FsAddSectionComponent,
        HighlightComponent,
        YoutubePlayerComponent,
        CiteComponent,
        AudioComponent,
        PictureComponent,
        CtaComponent,
        FormTabletComponent,
        ModalComponent,
        SewWsPageContentTeaserSimple,
        SewWsPodcastHumian,
    ],
})
export class ContentComponent extends OnDestroyBase implements OnInit, OnDestroy, OnChanges {
    @Input() public contents: Content[] = [];
    @Input() public margin: Margin[] = [];
    @Input() public breakpoint: CustomBreakpoint = 0;

    // labels
    public labels?: BlogLabels;
    public txtFormHeader?: string;
    public txtSocialMedia?: string;
    public addSectionLbl?: string;
    public podcastCloseAriaLabel?: string;
    public podcastForwardAriaLabel?: string;
    public podcastHeadlinePodcasts?: string;
    public podcastMuteAriaLabel?: string;
    public podcastPlayAriaLabel?: string;
    public podcastRewindAriaLabel?: string;

    public showSMBottom = false;
    public showForm = false;
    public showPodcast = false;
    public isPreview = false;
    public podcastMargin?: Margin;
    public podcasts: { url: string; icon: string }[] = [];

    public constructor(
        private modalService: ModalService,
        private blogService: BlogService,
        private breakpointObserver: BreakpointObserver
    ) {
        super();
    }

    public ngOnInit(): void {
        this.isPreview = environment.preview;
        this.updateMarginState();
        // if first content element is not of Type text, formular and social media should be displayed at bottom
        if (this.contents.length) {
            if (this.contents[0].type !== ContentType.TEXT) {
                this.showSMBottom = true;
            }
        }

        // special breakpoint for subscribe form
        this.breakpointObserver
            .observe(['(min-width: 1204px)'])
            .pipe(takeUntil(this.destroySubject))
            .subscribe(({ matches }) => {
                this.showSMBottom = !matches;
            });

        this.initLabels();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.margin) {
            this.updateMarginState();
        }
    }

    public openSubscribeDialog(id: string): void {
        this.modalService.open(id);
    }

    public closeSubscribeDialog(id: string): void {
        this.modalService.close(id);
    }

    private initLabels(): void {
        this.blogService.labelsUpdated.pipe(takeUntil(this.destroySubject)).subscribe((labels) => {
            this.labels = labels;
            this.mapLabels(labels);
        });
    }

    private mapLabels(labels: BlogLabels): void {
        this.txtFormHeader = labels.subscribeHeadline;
        this.txtSocialMedia = labels.social_media;
        this.addSectionLbl = labels.create_section;
        this.podcastCloseAriaLabel = labels.podcastCloseAriaLabel;
        this.podcastForwardAriaLabel = labels.podcastForwardAriaLabel;
        this.podcastHeadlinePodcasts = labels.podcastHeadlinePodcasts;
        this.podcastMuteAriaLabel = labels.podcastMuteAriaLabel;
        this.podcastPlayAriaLabel = labels.podcastPlayAriaLabel;
        this.podcastRewindAriaLabel = labels.podcastRewindAriaLabel;
    }

    private updateMarginState(): void {
        this.podcastMargin = this.margin.find((entry) => entry.type === 'podcast');
        this.showPodcast = Boolean(this.podcastMargin);
        this.showForm = !this.showPodcast;
        this.podcasts = (this.podcastMargin?.channels ?? []).map((channel) => ({
            url: channel.url,
            icon: channel.icon,
        }));
    }
}
